import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMenuOpen(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white/95 backdrop-blur-sm shadow-sm">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <img 
                src="https://public.readdy.ai/ai/img_res/31da2322-488a-4d1c-b3ef-653fb3d1c721.png" 
                alt="PETRO ENGENHARIA LTDA" 
                className="h-12 w-auto"
              />
              <div className="hidden md:block">
                <div className="text-lg font-bold text-gray-900">PETRO ENGENHARIA</div>
                <div className="text-xs text-gray-600">Secure VPN Solutions</div>
              </div>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-8">
              <button onClick={() => scrollToSection('home')} className="text-gray-700 hover:text-blue-600 transition-colors whitespace-nowrap cursor-pointer">
                Home
              </button>
              <button onClick={() => scrollToSection('about')} className="text-gray-700 hover:text-blue-600 transition-colors whitespace-nowrap cursor-pointer">
                About
              </button>
              <button onClick={() => scrollToSection('vpn-service')} className="text-gray-700 hover:text-blue-600 transition-colors whitespace-nowrap cursor-pointer">
                VPN Service
              </button>
              <button onClick={() => scrollToSection('contact')} className="text-gray-700 hover:text-blue-600 transition-colors whitespace-nowrap cursor-pointer">
                Contact
              </button>
              <button onClick={() => scrollToSection('support')} className="text-gray-700 hover:text-blue-600 transition-colors whitespace-nowrap cursor-pointer">
                Support
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button 
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden p-2 cursor-pointer"
            >
              <i className={`ri-${isMenuOpen ? 'close' : 'menu'}-line text-2xl text-gray-900`}></i>
            </button>
          </div>

          {/* Mobile Menu */}
          {isMenuOpen && (
            <div className="lg:hidden py-4 border-t border-gray-200">
              <div className="flex flex-col gap-4">
                <button onClick={() => scrollToSection('home')} className="text-gray-700 hover:text-blue-600 transition-colors text-left cursor-pointer">
                  Home
                </button>
                <button onClick={() => scrollToSection('about')} className="text-gray-700 hover:text-blue-600 transition-colors text-left cursor-pointer">
                  About
                </button>
                <button onClick={() => scrollToSection('vpn-service')} className="text-gray-700 hover:text-blue-600 transition-colors text-left cursor-pointer">
                  VPN Service
                </button>
                <button onClick={() => scrollToSection('contact')} className="text-gray-700 hover:text-blue-600 transition-colors text-left cursor-pointer">
                  Contact
                </button>
                <button onClick={() => scrollToSection('support')} className="text-gray-700 hover:text-blue-600 transition-colors text-left cursor-pointer">
                  Support
                </button>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center justify-center pt-20 bg-white">
        <div className="absolute inset-0 overflow-hidden">
          <img 
            src="https://public.readdy.ai/ai/img_res/51f649590b533262433bf831f52818c5.jpg"
            alt="Network Security Background"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-white/40 via-white/30 to-white/40"></div>
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-6 text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/90 backdrop-blur-sm rounded-full shadow-sm mb-8">
            <i className="ri-shield-check-line text-blue-600"></i>
            <span className="text-sm font-medium text-gray-800">Enterprise-Grade Security</span>
          </div>

          {/* Main Headline */}
          <h1 className="text-6xl lg:text-8xl font-bold text-gray-900 mb-6 leading-tight">
            Secure Your Digital<br />World with PETRO VPN
          </h1>

          {/* Subheadline */}
          <p className="text-lg lg:text-xl text-gray-700 mb-12 max-w-3xl mx-auto">
            Professional VPN solution engineered for maximum privacy and performance
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button 
              onClick={() => scrollToSection('contact')}
              className="px-8 py-4 bg-blue-900 text-white rounded-lg font-medium hover:bg-blue-800 transition-all hover:scale-105 shadow-lg whitespace-nowrap cursor-pointer"
            >
              Get Started
            </button>
            <button 
              onClick={() => scrollToSection('about')}
              className="px-8 py-4 bg-white text-blue-900 border-2 border-blue-900 rounded-lg font-medium hover:bg-blue-50 transition-all whitespace-nowrap cursor-pointer"
            >
              Learn More
            </button>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Column - Image */}
            <div className="relative">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <div className="w-full h-96">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Modern%20server%20infrastructure%20and%20data%20center%20with%20high-tech%20equipment%2C%20professional%20technology%20visualization%20showing%20secure%20network%20systems%2C%20clean%20corporate%20style%20with%20blue%20lighting%2C%20enterprise-grade%20security%20concept%2C%20realistic%20photography%20with%20depth&width=800&height=600&seq=about-img-001&orientation=landscape"
                    alt="PETRO ENGENHARIA Infrastructure"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                
                {/* Floating Badges */}
                <div className="absolute top-6 left-6 flex flex-col gap-3">
                  <div className="px-4 py-2 bg-white/80 backdrop-blur-md rounded-xl shadow-lg">
                    <span className="text-sm font-semibold text-gray-800">Brazilian Engineering</span>
                  </div>
                  <div className="px-4 py-2 bg-white/80 backdrop-blur-md rounded-xl shadow-lg">
                    <span className="text-sm font-semibold text-gray-800">ISO Certified</span>
                  </div>
                  <div className="px-4 py-2 bg-white/80 backdrop-blur-md rounded-xl shadow-lg">
                    <span className="text-sm font-semibold text-gray-800">24/7 Support</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Content */}
            <div className="relative">
              <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-blue-600 to-blue-400 rounded-full"></div>
              <div className="pl-8">
                <div className="text-xs uppercase tracking-widest text-blue-600 font-semibold mb-4">
                  About Our Company
                </div>
                
                <h2 className="text-5xl font-bold text-gray-900 mb-6">
                  Engineering Excellence from Brazil
                </h2>

                <div className="flex flex-wrap gap-3 mb-8">
                  <span className="px-4 py-1 border border-gray-300 rounded-full text-sm text-gray-700">
                    Established 2020
                  </span>
                  <span className="px-4 py-1 text-sm text-gray-700">
                    Aparecida de Goiânia
                  </span>
                  <span className="px-4 py-1 text-sm text-gray-700">
                    Global Reach
                  </span>
                </div>

                <div className="space-y-4 text-gray-700 leading-relaxed">
                  <p>
                    PETRO ENGENHARIA LTDA is a Brazilian technology company dedicated to providing enterprise-grade VPN solutions that prioritize your privacy and security. Based in Aparecida de Goiânia, we combine local engineering excellence with global standards to deliver exceptional service.
                  </p>
                  <p>
                    Our commitment to privacy is unwavering. We believe that everyone deserves secure, unrestricted access to the internet without compromising their personal data. With military-grade encryption and a strict zero-log policy, we ensure your online activities remain completely private.
                  </p>
                  <p>
                    Built on cutting-edge technology and maintained by experienced engineers, PETRO VPN offers reliable, high-performance connections that protect your digital life across all your devices.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* VPN Service Features */}
      <section id="vpn-service" className="py-32 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-20">
            <h2 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-4">
              Why Choose PETRO VPN 🛡️
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {/* Feature 1 */}
            <div className="group relative rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105 cursor-pointer">
              <div className="relative h-96">
                <img 
                  src="https://readdy.ai/api/search-image?query=Abstract%20visualization%20of%20encrypted%20data%20streams%20with%20AES-256%20encryption%20symbols%2C%20digital%20security%20concept%20with%20flowing%20binary%20code%20and%20lock%20icons%2C%20dark%20blue%20and%20cyan%20color%20scheme%2C%20high-tech%20cybersecurity%20illustration%2C%20modern%20minimalist%20style&width=600&height=800&seq=feature-1-001&orientation=portrait"
                  alt="Military-Grade Encryption"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
                
                {/* Technical Badge */}
                <div className="absolute top-4 right-4 px-3 py-1 bg-blue-600 text-white text-xs font-bold rounded-full">
                  AES-256
                </div>

                {/* Content */}
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <h3 className="text-3xl font-bold text-white mb-3">
                    Military-Grade Encryption
                  </h3>
                  <p className="text-white/90 leading-relaxed">
                    Your data is protected with AES-256 encryption, the same standard used by governments and military organizations worldwide. Support for OpenVPN and WireGuard protocols ensures maximum security and performance.
                  </p>
                </div>
              </div>
            </div>

            {/* Feature 2 */}
            <div className="group relative rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105 cursor-pointer">
              <div className="relative h-96">
                <img 
                  src="https://readdy.ai/api/search-image?query=Global%20network%20visualization%20with%20connected%20server%20nodes%20across%20world%20map%2C%20international%20connectivity%20concept%20with%20glowing%20connection%20lines%2C%20blue%20and%20purple%20gradient%2C%20digital%20world%20map%20with%20data%20points%2C%20modern%20tech%20illustration&width=600&height=800&seq=feature-2-001&orientation=portrait"
                  alt="Global Server Network"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
                
                {/* Technical Badge */}
                <div className="absolute top-4 right-4 px-3 py-1 bg-green-600 text-white text-xs font-bold rounded-full">
                  Zero-Log
                </div>

                {/* Content */}
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <h3 className="text-3xl font-bold text-white mb-3">
                    Global Server Network
                  </h3>
                  <p className="text-white/90 leading-relaxed">
                    Access optimized servers strategically located across continents for low-latency connections. Experience fast, reliable VPN service no matter where you are in the world.
                  </p>
                </div>
              </div>
            </div>

            {/* Feature 3 */}
            <div className="group relative rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105 cursor-pointer">
              <div className="relative h-96">
                <img 
                  src="https://readdy.ai/api/search-image?query=Privacy%20protection%20concept%20with%20shield%20and%20lock%20symbols%2C%20no%20tracking%20or%20logging%20visualization%2C%20secure%20private%20browsing%20illustration%2C%20dark%20background%20with%20teal%20and%20blue%20accents%2C%20digital%20privacy%20and%20anonymity%20theme%2C%20clean%20modern%20design&width=600&height=800&seq=feature-3-001&orientation=portrait"
                  alt="Zero-Log Policy"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
                
                {/* Technical Badge */}
                <div className="absolute top-4 right-4 px-3 py-1 bg-purple-600 text-white text-xs font-bold rounded-full">
                  Multi-Protocol
                </div>

                {/* Content */}
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <h3 className="text-3xl font-bold text-white mb-3">
                    Zero-Log Policy
                  </h3>
                  <p className="text-white/90 leading-relaxed">
                    We guarantee complete privacy with our strict zero-log policy. We don't track, collect, or store any of your online activities. Your privacy is our top priority.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="relative">
        <div className="grid lg:grid-cols-2 min-h-screen">
          {/* Left Half - Dark */}
          <div className="relative bg-slate-900 p-12 lg:p-20 flex items-center">
            <div className="absolute inset-0 opacity-10">
              <div className="absolute inset-0" style={{
                backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(255,255,255,.05) 35px, rgba(255,255,255,.05) 70px)'
              }}></div>
            </div>
            
            <div className="relative z-10 max-w-xl">
              <div className="text-xs uppercase tracking-widest text-cyan-400 font-semibold mb-8">
                GET IN TOUCH
              </div>
              
              <h2 className="text-6xl lg:text-7xl font-bold text-white leading-tight mb-4" style={{ letterSpacing: '0.02em' }}>
                Connect With<br />
                <span className="text-7xl lg:text-8xl">PETRO</span><br />
                <span className="text-7xl lg:text-8xl">ENGENHARIA</span>
              </h2>
            </div>
          </div>

          {/* Right Half - White */}
          <div className="bg-white p-12 lg:p-20 flex items-center justify-center">
            <div className="w-full max-w-md">
              <h3 className="text-4xl font-bold text-gray-900 mb-12 text-center">
                Contact Information
              </h3>

              <div className="space-y-5">
                {/* Email Card */}
                <div className="bg-gray-50 rounded-2xl p-6 border border-gray-200">
                  <div className="flex items-start gap-4">
                    <div className="w-6 h-6 flex items-center justify-center">
                      <i className="ri-mail-line text-2xl text-blue-600"></i>
                    </div>
                    <div className="flex-1">
                      <div className="text-xs uppercase tracking-wide text-gray-600 mb-1">
                        EMAIL
                      </div>
                      <a href="mailto:information@petroengenharialtda.com" className="text-gray-900 font-medium hover:text-blue-600 transition-colors cursor-pointer">
                        information@petroengenharialtda.com
                      </a>
                    </div>
                  </div>
                </div>

                {/* Phone Card */}
                <div className="bg-gray-50 rounded-2xl p-6 border border-gray-200">
                  <div className="flex items-start gap-4">
                    <div className="w-6 h-6 flex items-center justify-center">
                      <i className="ri-phone-line text-2xl text-blue-600"></i>
                    </div>
                    <div className="flex-1">
                      <div className="text-xs uppercase tracking-wide text-gray-600 mb-1">
                        PHONE
                      </div>
                      <a href="tel:+17087873831" className="text-gray-900 font-medium hover:text-blue-600 transition-colors cursor-pointer">
                        +1 708 787 3831
                      </a>
                    </div>
                  </div>
                </div>

                {/* Address Card */}
                <div className="bg-gray-50 rounded-2xl p-6 border border-gray-200">
                  <div className="flex items-start gap-4">
                    <div className="w-6 h-6 flex items-center justify-center">
                      <i className="ri-map-pin-line text-2xl text-blue-600"></i>
                    </div>
                    <div className="flex-1">
                      <div className="text-xs uppercase tracking-wide text-gray-600 mb-1">
                        ADDRESS
                      </div>
                      <div className="text-gray-900 font-medium leading-relaxed">
                        Rua RIO DAS FLORES SN QUADRA284 LOTE 06<br />
                        APARECIDA DE GOIANIA, Brazil<br />
                        ZIP 74942-400
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Support Section */}
      <section id="support" className="py-32 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-5xl font-bold text-gray-900 mb-6">
            Need Help?
          </h2>
          <p className="text-xl text-gray-700 mb-12 max-w-2xl mx-auto">
            Our dedicated support team is available 24/7 to assist you with any questions or technical issues. We're here to ensure your VPN experience is seamless and secure.
          </p>
          
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="p-8 bg-gray-50 rounded-2xl">
              <div className="w-12 h-12 flex items-center justify-center mx-auto mb-4">
                <i className="ri-customer-service-2-line text-4xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">24/7 Support</h3>
              <p className="text-gray-600">
                Round-the-clock assistance whenever you need it
              </p>
            </div>

            <div className="p-8 bg-gray-50 rounded-2xl">
              <div className="w-12 h-12 flex items-center justify-center mx-auto mb-4">
                <i className="ri-book-open-line text-4xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Documentation</h3>
              <p className="text-gray-600">
                Comprehensive guides and tutorials for all features
              </p>
            </div>

            <div className="p-8 bg-gray-50 rounded-2xl">
              <div className="w-12 h-12 flex items-center justify-center mx-auto mb-4">
                <i className="ri-question-answer-line text-4xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">FAQ</h3>
              <p className="text-gray-600">
                Quick answers to common questions
              </p>
            </div>
          </div>

          <button 
            onClick={() => scrollToSection('contact')}
            className="px-10 py-4 bg-slate-900 text-white rounded-full font-medium hover:bg-slate-800 transition-all hover:scale-105 shadow-lg inline-flex items-center gap-2 whitespace-nowrap cursor-pointer"
          >
            Contact Support
            <i className="ri-arrow-right-line"></i>
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-emerald-900 text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-8">
          <div className="grid md:grid-cols-4 gap-8 items-center">
            {/* Brand */}
            <div>
              <img 
                src="https://public.readdy.ai/ai/img_res/31da2322-488a-4d1c-b3ef-653fb3d1c721.png" 
                alt="PETRO ENGENHARIA LTDA" 
                className="h-10 mb-2 brightness-0 invert"
              />
              <p className="text-sm text-gray-300">Secure VPN Solutions</p>
            </div>

            {/* Quick Links */}
            <div>
              <div className="text-xs uppercase tracking-wide text-gray-400 mb-3">NAVIGATION</div>
              <div className="flex flex-wrap gap-2 text-sm text-gray-200">
                <button onClick={() => scrollToSection('home')} className="hover:text-white transition-colors cursor-pointer">Home</button>
                <span>|</span>
                <button onClick={() => scrollToSection('about')} className="hover:text-white transition-colors cursor-pointer">About</button>
                <span>|</span>
                <button onClick={() => scrollToSection('vpn-service')} className="hover:text-white transition-colors cursor-pointer">Features</button>
              </div>
            </div>

            {/* Legal */}
            <div>
              <div className="text-xs uppercase tracking-wide text-gray-400 mb-3">LEGAL</div>
              <div className="flex flex-wrap gap-2 text-sm text-gray-200">
                <a href="#" className="hover:text-white transition-colors cursor-pointer">Privacy Policy</a>
                <span>|</span>
                <a href="#" className="hover:text-white transition-colors cursor-pointer">Terms of Service</a>
              </div>
            </div>

            {/* Location */}
            <div>
              <div className="text-xs uppercase tracking-wide text-gray-400 mb-3">LOCATION</div>
              <p className="text-sm text-gray-300">Aparecida de Goiânia, Brazil</p>
            </div>
          </div>

          <div className="border-t border-white/10 mt-8 pt-6 text-center">
            <p className="text-xs text-gray-400">
              © 2025 PETRO ENGENHARIA LTDA. All rights reserved. | <a href="https://readdy.ai/?origin=logo" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors cursor-pointer">Website Builder</a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}